import { useResetPassword } from '@myrentalmate/api-client';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { apiErrorMessage } from '../../lib/apiError';
import { brand } from '../../lib/brand';
import { useFragmentToken } from '../../lib/useFragmentToken';

/** /reset-password#<token>. It starts no session: you then sign in, two-factor included. */
export function ResetPasswordPage() {
  const token = useFragmentToken();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const reset = useResetPassword();

  function submit(event: FormEvent) {
    event.preventDefault();
    const differs = password !== confirm;
    setMismatch(differs);
    if (!differs) reset.mutate({ data: { token, password } });
  }

  const invalid = !token || (reset.error as { status?: number } | null)?.status === 404;
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%' }}>
        <Stack spacing={2}>
          <Typography variant="h4" sx={{ textAlign: 'center' }}>
            {brand.name}
          </Typography>
          {reset.isSuccess ? (
            <>
              <Alert severity="success">Your password was changed.</Alert>
              <Button variant="contained" component={Link} to="/">
                Sign in
              </Button>
            </>
          ) : invalid ? (
            <>
              <Typography color="text.secondary">
                This link isn't valid. It may have expired or already been used. You can ask for a
                new one from the sign-in page.
              </Typography>
              <Button component={Link} to="/">
                Go to sign in
              </Button>
            </>
          ) : (
            <Stack component="form" spacing={2} onSubmit={submit}>
              <Typography color="text.secondary">Choose a new password.</Typography>
              {mismatch && <Alert severity="error">Those two passwords don't match.</Alert>}
              {reset.isError && <Alert severity="error">{apiErrorMessage(reset.error)}</Alert>}
              <TextField
                label="New Password"
                type="password"
                required
                autoComplete="new-password"
                helperText="At least 12 characters. A short phrase works well"
                slotProps={{ htmlInput: { minLength: 12 } }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <TextField
                label="New Password Again"
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              <Button type="submit" variant="contained" size="large" disabled={reset.isPending}>
                {reset.isPending ? 'Saving…' : 'Change password'}
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
